import { ComputedRef } from 'vue'

import { JudgeRecord } from '@/domain/entities/judge/judgeRecord'
import { DisplayLanguageType } from '@/domain/value_objects/language/displayLanguageType'
import { HeatMapViewModel } from '@/presentation/viewmodels/dashboard/heatMapViewModel'

export class HeatMapPresenter {
  private viewModel: HeatMapViewModel

  private yearAgo: Date
  private yearAgoSun: Date
  private todayNextSat: Date
  private acList: any[] //ACした日のみのリスト
  private everyAcList: { [index: string]: number } //ACした日以外も含むリスト
  private weekList: any[] = [[], [], [], [], [], [], []] //週ごとのACリスト
  private xlabelMap: { [index: string]: boolean }

  constructor(viewModel: HeatMapViewModel) {
    this.viewModel = viewModel
    this.yearAgo = new Date()
    this.yearAgo.setFullYear(this.yearAgo.getFullYear() - 1)
    this.yearAgoSun = this.getLastSunday(this.yearAgo)
    this.todayNextSat = this.getNextSaturday(new Date())
    this.acList = []
    this.everyAcList = {}
    this.weekList = [[], [], [], [], [], [], []]
    this.xlabelMap = {}
  }

  get userJudgeRecordList(): ComputedRef<JudgeRecord[]> {
    return this.viewModel.userJudgeRecordList
  }

  get heatMapXLabel(): ComputedRef<string[]> {
    return this.viewModel.heatMapXLabel
  }

  get heatMapSeries(): ComputedRef<any[]> {
    return this.viewModel.heatMapSeries
  }

  get isLoading(): ComputedRef<boolean> {
    return this.viewModel.isLoading
  }

  get error(): ComputedRef<string | null> {
    return this.viewModel.error
  }

  async getUserJudgeRecord(userId: string): Promise<void> {
    await this.viewModel.getUserJudgeRecord(userId)
  }

  getHeatMapData = async (userId: string, displayLang: DisplayLanguageType) => {
    await this.viewModel.getUserJudgeRecord(userId)
    this.initHeatMap()
    const heatMapSeries: any[] = []
    const heatMapXLabel: string[] = []

    this.viewModel.setHeatMapSeries([])
    this.viewModel.setHeatMapXLabel([])

    this.getAcList()

    for (let i = 0; i < this.acList.length; i++) {
      //acListをeveryAcListに貼り付け
      this.everyAcList[this.acList[i].date] = this.acList[i].count
    }

    let loop = new Date(this.yearAgoSun)
    const end = new Date(this.todayNextSat)
    while (loop <= end) {
      //追加されなかった残りの日を追加
      const dateStr: string = this.formatDate(loop)
      if (!this.everyAcList[dateStr]) {
        this.everyAcList[dateStr] = 0
      }
      const newDate = loop.setDate(loop.getDate() + 1)
      loop = new Date(newDate)
    }

    const pairs = Object.entries(this.everyAcList) //everyAcListをソート

    pairs.sort((p1, p2) => {
      const p1Key = p1[0]
      if (p1Key < p2[0]) {
        return -1
      }
      if (p1Key > p2[0]) {
        return 1
      }
      return 0
    })
    this.everyAcList = Object.fromEntries(pairs)

    for (const key in this.everyAcList) {
      //weekListにeveryAcListのデータを追加
      const day = new Date(key).getDay()
      this.weekList[day].push({
        x: key,
        y: this.everyAcList[key]
      })
    }

    for (let i = 0; i < 7; i++) {
      //seriesにweekListのデータを追加
      heatMapSeries.push({ name: this.getDay(i, displayLang), data: this.weekList[i] })
    }

    for (let i = 0; i < heatMapSeries[0].data.length; i++) {
      //ヒートマップのx軸ラベルの月表示を制御
      const str =
        new Date(heatMapSeries[0].data[i].x).getFullYear().toString() +
        '-' +
        (new Date(heatMapSeries[0].data[i].x).getMonth() + 1).toString()
      if (!this.xlabelMap[str]) {
        this.xlabelMap[str] = true
        heatMapXLabel.push(this.getMonth(new Date(heatMapSeries[0].data[i].x).getMonth() + 1, displayLang))
      } else {
        heatMapXLabel.push('')
      }
    }

    this.viewModel.setHeatMapXLabel(heatMapXLabel)
    this.viewModel.setHeatMapSeries(heatMapSeries)
  }

  formatDate = (date: Date) => {
    const formatted_date =
      date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2)
    return formatted_date
  }

  getDay = (date: number, displayLang: DisplayLanguageType) => {
    if (date == 0) {
      return displayLang === 'Japanese' ? '日曜日' : 'Sat'
    } else if (date == 1) {
      return displayLang === 'Japanese' ? '月曜日' : 'Mon'
    } else if (date == 2) {
      return displayLang === 'Japanese' ? '火曜日' : 'Tue'
    } else if (date == 3) {
      return displayLang === 'Japanese' ? '水曜日' : 'Wed'
    } else if (date == 4) {
      return displayLang === 'Japanese' ? '木曜日' : 'Thu'
    } else if (date == 5) {
      return displayLang === 'Japanese' ? '金曜日' : 'Fri'
    } else if (date == 6) {
      return displayLang === 'Japanese' ? '土曜日' : 'Sat'
    }
  }

  getMonth = (date: number, displayLang: DisplayLanguageType) => {
    if (date == 1) {
      return displayLang === 'Japanese' ? '1月' : 'Jan'
    } else if (date == 2) {
      return displayLang === 'Japanese' ? '2月' : 'Feb'
    } else if (date == 3) {
      return displayLang === 'Japanese' ? '3月' : 'Mar'
    } else if (date == 4) {
      return displayLang === 'Japanese' ? '4月' : 'Apr'
    } else if (date == 5) {
      return displayLang === 'Japanese' ? '5月' : 'May'
    } else if (date == 6) {
      return displayLang === 'Japanese' ? '6月' : 'Jun'
    } else if (date == 7) {
      return displayLang === 'Japanese' ? '7月' : 'Jul'
    } else if (date == 8) {
      return displayLang === 'Japanese' ? '8月' : 'Aug'
    } else if (date == 9) {
      return displayLang === 'Japanese' ? '9月' : 'Sep'
    } else if (date == 10) {
      return displayLang === 'Japanese' ? '10月' : 'Oct'
    } else if (date == 11) {
      return displayLang === 'Japanese' ? '11月' : 'Nov'
    } else if (date == 12) {
      return displayLang === 'Japanese' ? '12月' : 'Dec'
    } else {
      return ''
    }
  }

  getLastSunday = (d: Date) => {
    const t = new Date(d)
    t.setDate(t.getDate() - t.getDay())
    return t
  }

  getNextSaturday = (d: Date) => {
    const t = new Date(d)
    t.setDate(t.getDate() + ((6 - t.getDay()) % 7))
    return t
  }

  getAcList = () => {
    let acCount = 1
    let lastDay = '-1'

    for (let i = 0; i < this.viewModel.userJudgeRecordList.value.length; i++) {
      if (this.viewModel.userJudgeRecordList.value[i].judgeDate < new Date(this.yearAgo)) break
      if (this.viewModel.userJudgeRecordList.value[i].status != 'ACCEPTED') continue
      const nowDay = this.formatDate(this.viewModel.userJudgeRecordList.value[i].judgeDate)
      if (lastDay == nowDay) {
        acCount++
      } else {
        if (lastDay != '-1') {
          this.acList.push({ date: lastDay, count: acCount })
        }
        acCount = 1
        lastDay = nowDay
      }
    }
    if (lastDay != '-1') this.acList.push({ date: lastDay, count: acCount })
  }

  initHeatMap = () => {
    this.yearAgo = new Date()
    this.yearAgo.setFullYear(this.yearAgo.getFullYear() - 1)
    this.yearAgoSun = this.getLastSunday(this.yearAgo)
    this.todayNextSat = this.getNextSaturday(new Date())
    this.acList = []
    this.everyAcList = {}
    this.weekList = [[], [], [], [], [], [], []]
    this.xlabelMap = {}
  }
}
